/**
 * RPC Health Check Utility - Simplified and Less Noisy
 */

import { supabase } from './supabaseClient';

// List of critical RPCs that the app actually uses
// Only check lightweight health RPCs, not privileged data functions
const REQUIRED = ['get_rpc_health_status']; // only check health status, not data functions
let warned: Set<string> = new Set();

interface RPCHealthStatus {
  function_name: string;
  exists: boolean;
  last_error?: string;
  error_count: number;
  last_success?: string;
}

/**
 * Check the health of RPC functions (harmless - only checks health status)
 */
export async function checkRPCHealth(): Promise<{ ok: boolean; details: any }> {
  try {
    const { data, error } = await supabase.rpc('get_rpc_health_status');
    return { ok: !!data?.ok && !error, details: error ?? null };
  } catch (error) {
    console.error('[RPC Health] Health check failed:', error);
    return { ok: false, details: error };
  }
}

/**
 * Get RPC health status for the admin dashboard
 */
export async function getRPCHealthStatus(): Promise<RPCHealthStatus[]> {
  try {
    const { data, error } = await supabase.rpc('get_rpc_health_status');
    
    if (error) {
      console.warn('[RPC Health] get_rpc_health_status not available:', error.message);
      return [];
    }
    
    return data?.functions || [];
  } catch (error) {
    console.warn('[RPC Health] Failed to get health status:', error);
    return [];
  }
}

/**
 * Initialize RPC health monitoring (call once on app start)
 */
export async function initializeRPCHealthMonitoring(): Promise<void> {
  try {
    console.log('[RPC Health] Initializing RPC health monitoring...');
    await checkRPCHealth();
    console.log('[RPC Health] RPC health monitoring initialized');
  } catch (error) {
    console.error('[RPC Health] Failed to initialize RPC health monitoring:', error);
  }
}